import { orderService } from "@/services/orderServices";
import { OrderState, PayOrderAmount, PayPartialAmount } from "@/types/orders";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Initial State
const initialState: OrderState = {
  orderData: null,
  userOrders: [],
  invoiceData: null,
  loading: false,
  error: null,
};


// ✅ View Order API
export const viewOrder = createAsyncThunk(
  "order/viewOrder",
  async (orderNumber: string, { rejectWithValue }) => {
    try {
      return await orderService.viewOrder(orderNumber);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching order details");
    }
  }
);


// ✅ Get Orders by User API
export const getOrdersByUser = createAsyncThunk(
  "order/getOrdersByUser",
  async (UserID: string | null, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByUser(UserID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching user orders");
    }
  }
);

// ✅ Generate Invoice API
export const generateOrderInvoice = createAsyncThunk(
  "order/generateOrderInvoice",
  async (orderNumber: string, { rejectWithValue }) => {
    try {
      return await orderService.generateOrderInvoice(orderNumber);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error generating invoice");
    }
  }
);

// ✅ Pay Order Amount
export const payOrderAmount = createAsyncThunk(
  "order/payOrderAmount",
  async (data: PayOrderAmount, { rejectWithValue }) => {
    try {
      return await orderService.payOrderAmount(data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error generating invoice");
    }
  }
);

// ✅ Pay Partial Amount
export const payPartialAmount = createAsyncThunk(
  "order/payPartialAmount",
  async (data: PayPartialAmount, { rejectWithValue }) => {
    try {
      return await orderService.payPartialAmount(data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error generating invoice");
    }
  }
);

// ✅ Order Slice
const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(viewOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(viewOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderData = action.payload;
      })
      .addCase(viewOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(getOrdersByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrdersByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userOrders = action.payload?.orders;
      })
      .addCase(getOrdersByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(generateOrderInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateOrderInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceData = action.payload;
      })
      .addCase(generateOrderInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default orderSlice.reducer;
